import * as THREE from "three/webgpu";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import { HDRLoader } from "three/addons/loaders/HDRLoader.js";
import { SunLight } from "three/addons/lights/SunLight.js";
import { SunLightNode } from "three/addons/lights/SunLightNode.js";
import { pass, mrt, output, normalView, materialMetalness, materialRoughness, screenUV, sample, packNormalToRGB, unpackRGBToNormal, velocity, diffuseColor, float, vec2, vec3, vec4, builtinShadowContext, builtinAOContext, texture, uniform, renderOutput, saturation, Fn, reference, renderGroup, frameId, screenCoordinate, interleavedGradientNoise, vogelDiskSample, convertToTexture } from "three/tsl";
import { gaussianBlur } from "three/addons/tsl/display/GaussianBlurNode.js";
import { ao } from "three/addons/tsl/display/GTAONode.js";
import { denoise } from "three/addons/tsl/display/DenoiseNode.js";
import { ssr } from "three/addons/tsl/display/SSRNode.js";
import { temporalReproject } from "three/addons/tsl/display/TemporalReprojectNode.js";
import { recurrentDenoise } from "three/addons/tsl/display/RecurrentDenoiseNode.js";
import { sss } from "three/addons/tsl/display/SSSNode.js";
import { traa } from "three/addons/tsl/display/TRAANode.js";
import { sharpen } from "three/addons/tsl/display/SharpenNode.js";
import { LATEX, FINISHES, LATEX_IOR, FINISH_CODES } from "./flowerspop-finishes.js?v=53abea9732";
const { WebGPURenderer, MeshPhysicalNodeMaterial, ShadowNodeMaterial, PMREMGenerator: PMREMGeneratorGPU } = THREE;

// app.js owns the configuration state; colors arrive via the form events flowerspop:parts
// (after every change) and flowerspop:get-parts (pulled once the model has loaded).
const form = document.querySelector("#configurator");
const card = document.querySelector(".preview-card");
const photo = card?.querySelector(".hero-photo");
// Tela de carregamento (logo + barra): download do modelo vale 75%, do HDRI 20%, e o
// primeiro quadro do 3D completa e esconde a tela.
const previewLoader = document.querySelector("#preview-loader");
const loadProgress = { model: 0, hdri: 0 };
function setLoadProgress(key, fraction) {
  loadProgress[key] = Math.max(loadProgress[key], Math.min(1, fraction));
  showLoadProgress(0.75 * loadProgress.model + 0.2 * loadProgress.hdri);
}
function showLoadProgress(value) {
  previewLoader?.style.setProperty("--progress", value);
  previewLoader?.setAttribute("aria-valuenow", String(Math.round(value * 100)));
}
const byteProgress = (key) => (event) => { if (event.lengthComputable) setLoadProgress(key, event.loaded / event.total); };
// Fundo = foto do estúdio, enquadrada como CSS `background-size: cover` × zoom e
// `background-position` (fração 0–1). Zoom > 1 aproxima a parede; posição puxada para
// baixo mantém o chão da foto sob o buquê, onde cai a sombra. Espelhado no CSS do .preview-card.
// No tema Shopify os arquivos ficam no CDN: window.FLOWERSPOP_ASSETS mapeia o caminho local
// ("/assets/models/blush-tulip.glb") para a URL publicada; sem o mapa, vale o caminho local.
const assetUrl = (path) => new URL(window.FLOWERSPOP_ASSETS?.[path.split("?")[0]] || path, document.baseURI).href;

const STUDIO_BACKGROUND = { url: "/assets/environments/studio-seamless-gray.jpg", zoom: 1.2, position: [0.5, 0.8] };
const studioBackground = new Image();
// No tema a foto vem do CDN da Shopify (outro domínio): sem CORS ela "suja" o canvas da
// captura e o Save image / Save PDF falham com SecurityError.
studioBackground.crossOrigin = "anonymous";
studioBackground.src = assetUrl(STUDIO_BACKGROUND.url);
let studioBackgroundTex = null;
let studioBackgroundLinearTex = null; // mesma foto decodificada (linear): fundo da cena 3D
const bgSize = uniform(new THREE.Vector2(1, 1)); // tamanho da foto em unidades do quadro, atualizado em fit()
const bgOffset = uniform(new THREE.Vector2(0, 0)); // canto superior esquerdo da foto no quadro
// Cor média da foto (linear): clear color da cena e referência de tinta do vidro (Crystal).
const backdropTint = uniform(new THREE.Color(0xffffff));
// Espalhamento da sombra do sol no chão: desvio padrão do blur como fração da altura do quadro.
const SHADOW_BLUR = { spread: 0.02, sigma: 6, resolutionScale: 0.25 };
const shadowBlurStep = uniform(1); // passo do blur em pixels do alvo reduzido, atualizado em updateBgCover()
const contactStrength = uniform(0.3); // expoente do AO de contato: 0 desliga, 1 = como assado, 2 = mais escuro
// Filtro de cor aplicado por último (espaço de tela), como um ajuste de foto: 1 = sem mudança.
// Leve redução de brilho e saturação e balanço de branco frio (R, G, B). O buquê esfria só
// pela metade para não puxar as cores do catálogo (rosa virava lilás).
const COLOR_GRADE = {
  background: { brightness: 1, saturation: 1, whiteBalance: [1, 1, 1] }, // foto na cor original
  bouquet: { brightness: 0.95, saturation: 0.92, whiteBalance: [0.975, 0.995, 1.02] },
};

const PART_MATERIALS =
  (typeof PRODUCT_CONFIG !== "undefined" && PRODUCT_CONFIG.materials) ||
  { petals: "MAT_Petals", center: "MAT_Center", stems: "MAT_Stems", bow: "MAT_Bow" };
const MODEL_URL =
  (typeof PRODUCT_CONFIG !== "undefined" && PRODUCT_CONFIG.model?.url) ||
  "/assets/models/blush-tulip.glb";

// WebGPU primeiro, WebGL de fallback (mesma API de materiais nos dois).
let renderer;
try {
  if (!navigator.gpu) throw new Error('WebGPU unavailable');
  renderer = new WebGPURenderer({ antialias: false, alpha: true, preserveDrawingBuffer: true });
  // TRAA exige MSAA desligado (docs do TRAANode): com antialias:true o depth do
  // scenePass tem sampleCount 4 e o history depth tem 1 -> CopyTextureToTexture falha.
  renderer.library.addLight(SunLightNode, SunLight);
  await renderer.init();
} catch {
  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
}
renderer.toneMapping = THREE.NeutralToneMapping;
renderer.toneMappingExposure = 1;
renderer.shadowMap.enabled = true;
// PCF: sombra do sol nítida no buquê; o espalhamento no chão é feito no pipeline (blur).
renderer.shadowMap.type = THREE.PCFShadowMap; // PCFSoft não existe no WebGPU
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
Object.assign(renderer.domElement.style, { position: "absolute", inset: "0", width: "100%", height: "100%" });
card?.prepend(renderer.domElement);

const scene = new THREE.Scene();
renderer.setClearColor(0xffffff, 0);
const PMREM = renderer.isWebGPURenderer ? PMREMGeneratorGPU : THREE.PMREMGenerator;
const pmrem = new PMREM(renderer);
let studioEnvironment;
// Foto do estúdio: fundo composto no pipeline e cor média (clear color / sombra no chão).
async function loadStudioEnv() {
  try {
    const tex = await new THREE.TextureLoader().loadAsync(studioBackground.src);
    tex.colorSpace = THREE.SRGBColorSpace;
    studioEnvironment = tex;
    // Fundo composto DENTRO do pipeline (máscara por depth): pixel exato, sem
    // tone mapping. NoColorSpace = sample cru, sem decode de cor.
    const bgT = tex.clone();
    bgT.colorSpace = THREE.NoColorSpace;
    bgT.mapping = THREE.UVMapping;
    bgT.flipY = false; // screenUV tem origem no topo: sem isso o fundo sai invertido
    bgT.needsUpdate = true;
    studioBackgroundTex = bgT;
    // A mesma foto (linear, mesmo enquadramento) como fundo da cena 3D: é o que aparece
    // através dos balões Crystal. No resultado final o fundo continua vindo de bgT, pixel-exato.
    const bgLinear = bgT.clone();
    bgLinear.colorSpace = THREE.SRGBColorSpace;
    bgLinear.needsUpdate = true;
    studioBackgroundLinearTex = bgLinear;
    scene.backgroundNode = texture(bgLinear, screenUV.sub(bgOffset).div(bgSize));
    updateBgCover();
    const probe = document.createElement("canvas").getContext("2d", { willReadFrequently: true });
    probe.canvas.width = probe.canvas.height = 1;
    probe.drawImage(tex.image, 0, 0, 1, 1); // média da foto inteira
    const [r, g, b] = probe.getImageData(0, 0, 1, 1).data;
    backdropTint.value.setRGB(r / 255, g / 255, b / 255, THREE.SRGBColorSpace);
    renderer.setClearColor(backdropTint.value, 1);
  } catch (err) {
    console.warn("[renderer] reference environment failed, using direct lights:", err);
  }
}
// Luz ambiente e reflexos nos balões = HDRI de estúdio (Poly Haven "Studio Kontrast 03", CC0):
// softboxes reais refletidos no látex. rotation (graus) gira o HDRI e muda onde os brilhos caem.
// Se o HDRI falhar, cai no estúdio gerado (RoomEnvironment). A foto continua sendo só o fundo.
const STUDIO_HDRI = { url: "/assets/environments/studio_kontrast_03_1k.hdr", rotation: 15 };
async function loadStudioHdri() {
  scene.environmentRotation.y = THREE.MathUtils.degToRad(STUDIO_HDRI.rotation);
  try {
    const hdr = await new HDRLoader().loadAsync(assetUrl(STUDIO_HDRI.url), byteProgress("hdri"));
    hdr.mapping = THREE.EquirectangularReflectionMapping;
    scene.environment = pmrem.fromEquirectangular(hdr).texture;
    hdr.dispose();
  } catch (err) {
    console.warn("[renderer] studio HDRI failed, using RoomEnvironment:", err);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  }
  setLoadProgress("hdri", 1);
}
const studioEnvReady = Promise.all([loadStudioEnv(), loadStudioHdri()]);
// Equilíbrio "softbox de estúdio" (referência dos copos-de-leite): o estúdio (RoomEnvironment)
// dá a luz macia e os brilhos largos; o sol dá direção e degradê; o preenchimento só abre sombras.
scene.environmentIntensity = 0.95;
const hemi = new THREE.HemisphereLight(0xffffff, 0x8d8d8d, 0.15); // leve: o HDRI já cobre (ajustável no painel)
hemi.position.set(0, 20, 0);
scene.add(hemi);
const sun = new THREE.DirectionalLight(0xffffff, 1.3);
// SSSNode só suporta DirectionalLight (lê light.target.matrixWorld — SunLight não tem target).
sun.castShadow = true;
sun.shadow.bias = -0.001;
sun.shadow.radius = 8;
sun.shadow.mapSize.set(1024, 1024);
// Penumbra sem granulado. O PCF do three no WebGPU usa só 5 amostras, giradas por um ruído fixo na
// tela: com raio 8 sobrava um granulado nas sombras (hastes de trás) que o TRAA não suaviza, porque
// o padrão não muda entre quadros. Aqui são 16 amostras em disco de Vogel e o giro muda a cada
// quadro (IGN temporal), então o TRAA faz a média. Mesma comparação de profundidade do PCF.
const SUN_SHADOW_SAMPLES = 16;
sun.shadow.filterNode = Fn(({ depthTexture, shadowCoord, shadow, depthLayer }) => {
  const mapSize = reference("mapSize", "vec2", shadow).setGroup(renderGroup);
  const radius = reference("radius", "float", shadow).setGroup(renderGroup);
  const spread = radius.div(mapSize.x);
  const phi = interleavedGradientNoise(screenCoordinate.xy.add(float(frameId).mod(64).mul(5.588238))).mul(Math.PI * 2);
  let lit = float(0);
  for (let i = 0; i < SUN_SHADOW_SAMPLES; i++) {
    let depth = texture(depthTexture, shadowCoord.xy.add(vogelDiskSample(i, SUN_SHADOW_SAMPLES, phi).mul(spread)));
    if (depthTexture.isArrayTexture) depth = depth.depth(depthLayer);
    lit = lit.add(depth.compare(shadowCoord.z));
  }
  return lit.div(SUN_SHADOW_SAMPLES);
});
sun.shadow.camera.near = 0.1;
sun.shadow.camera.far = 40;
scene.add(sun, sun.target);
// Fill neutro-frio e frontal, do lado oposto ao sol: só abre as sombras sem tingir as cores.
const fill = new THREE.DirectionalLight(0xf2f5ff, 0.8);
scene.add(fill, fill.target);

// Direções na vista frontal (+z). As luzes giram com a câmera, como num prato
// giratório: o fundo é uma foto fixa, então sol e sombra no chão ficam sempre
// no mesmo lado do quadro, de qualquer ângulo.
// Luz principal em graus, na vista frontal: azimute 0 = de frente, negativo = da esquerda
// (sombra cai para trás-direita); elevação = altura acima do chão. Valores do painel de ajuste.
const SUN_ANGLES = { azimuth: -16, elevation: 69 };
const sunDirection = new THREE.Vector3();
function aimSun() {
  const az = THREE.MathUtils.degToRad(SUN_ANGLES.azimuth), el = THREE.MathUtils.degToRad(SUN_ANGLES.elevation);
  sunDirection.set(Math.sin(az) * Math.cos(el), Math.sin(el), Math.cos(az) * Math.cos(el));
}
aimSun();
const LIGHT_RIG = [
  [sun, sunDirection],
  [fill, new THREE.Vector3(4, 3, 6).normalize()],
];
function alignLights() {
  if (!modelBounds) return;
  const azimuth = Math.atan2(camera.position.x - controls.target.x, camera.position.z - controls.target.z);
  for (const [light, direction] of LIGHT_RIG) {
    // Distância em raios do modelo: fica dentro do near/far da sombra definido em fit().
    light.position.copy(direction).applyAxisAngle(THREE.Object3D.DEFAULT_UP, azimuth)
      .multiplyScalar(modelBounds.radius * 4).add(controls.target);
    light.target.position.copy(controls.target);
  }
}

// Plano que só recebe sombra, no pé do buquê. Não grava depth: no pipeline o
// pixel continua sendo "fundo" e o alpha da sombra escurece a foto do chão.
const ShadowCatcherMaterial = renderer.isWebGPURenderer ? ShadowNodeMaterial : THREE.ShadowMaterial;
const shadowCatcher = new THREE.Mesh(
  new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2),
  new ShadowCatcherMaterial({ opacity: 0.19, depthWrite: false }), // sombra leve e difusa, estilo model-viewer
);
shadowCatcher.receiveShadow = true;
const ground = new THREE.Group();
ground.add(shadowCatcher);

// AO de contato "assado" da geometria: só onde o látex chega perto do chão. Cada vértice a
// menos de CONTACT_AO.height do piso escurece um disco pequeno sob ele (mais escuro e mais
// fechado quanto mais perto). Máximo por pixel, sem acumular: não vira mancha.
const CONTACT_AO = { height: 0.03, radius: 0.006, spread: 0.6, strength: 0.55, texels: 256 };
function bakeContactAO(model, floorY) {
  const { height, radius, spread, strength, texels } = CONTACT_AO;
  const points = [];
  const v = new THREE.Vector3();
  model.updateMatrixWorld(true);
  model.traverse((o) => {
    const pos = o.isMesh && o.geometry.attributes.position;
    if (!pos) return;
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(o.matrixWorld);
      const h = v.y - floorY;
      if (h < height) points.push(v.x, v.z, h);
    }
  });
  if (!points.length) return null;
  let [x0, z0, x1, z1] = [Infinity, Infinity, -Infinity, -Infinity];
  for (let i = 0; i < points.length; i += 3) {
    x0 = Math.min(x0, points[i]); x1 = Math.max(x1, points[i]);
    z0 = Math.min(z0, points[i + 1]); z1 = Math.max(z1, points[i + 1]);
  }
  const margin = radius + height * spread;
  const size = Math.max(x1 - x0, z1 - z0) + margin * 2;
  const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2, texel = size / texels;
  const occlusion = new Float32Array(texels * texels);
  for (let i = 0; i < points.length; i += 3) {
    const h = points[i + 2];
    const r = radius + h * spread; // mais alto = sombra mais aberta e mais fraca
    const peak = strength * (1 - h / height) ** 2;
    const px = (points[i] - cx + size / 2) / texel, pz = (points[i + 1] - cz + size / 2) / texel;
    const rp = r / texel;
    for (let y = Math.max(0, Math.floor(pz - rp)); y <= Math.min(texels - 1, Math.ceil(pz + rp)); y++) {
      for (let x = Math.max(0, Math.floor(px - rp)); x <= Math.min(texels - 1, Math.ceil(px + rp)); x++) {
        const d = Math.hypot(x + 0.5 - px, y + 0.5 - pz) / rp;
        if (d >= 1) continue;
        const a = peak * (1 - d * d) ** 2;
        const k = y * texels + x;
        if (a > occlusion[k]) occlusion[k] = a;
      }
    }
  }
  // Branco com o azul reduzido + blend multiplicativo: o contato escurece só o canal azul do
  // chão. O verde fica só com a sombra do sol, que o pipeline espalha; o contato segue nítido.
  const data = new Uint8Array(texels * texels * 4).fill(255);
  for (let k = 0; k < occlusion.length; k++) data[k * 4 + 2] = Math.round((1 - occlusion[k]) * 255);
  const map = new THREE.DataTexture(data, texels, texels);
  map.magFilter = map.minFilter = THREE.LinearFilter;
  map.needsUpdate = true;
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(size, size).rotateX(-Math.PI / 2),
    new THREE.MeshBasicMaterial({
      map, transparent: true, depthWrite: false,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
      blendSrc: THREE.ZeroFactor, blendDst: THREE.SrcColorFactor, // destino × cor da textura
      blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.OneFactor,
    }),
  );
  // Plano rotacionado -90° em X: v da textura cresce para -z; a linha 0 dos dados é z mínimo.
  map.flipY = false;
  mesh.scale.z = -1;
  mesh.position.set(cx, floorY + 0.0005, cz);
  mesh.renderOrder = 1;
  return mesh;
}

// Lente de 90 mm num sensor full-frame de 36 mm (convenção do Blender): tele de produto,
// ~22,6° no quadro quadrado, pouca distorção de perspectiva. Reaplicada em fit() a cada aspecto.
const CAMERA_FOCAL_MM = 90;
const camera = new THREE.PerspectiveCamera(35, 1, 0.01, 100);
camera.filmGauge = 36;

const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.enablePan = false;
controls.zoomSpeed = 0.6;
controls.minPolarAngle = THREE.MathUtils.degToRad(68);
controls.maxPolarAngle = THREE.MathUtils.degToRad(106);
let modelBounds;
let modelBox;
let postProcessing;
const FRAME_FILL = 0.82; // fração da altura do quadro que o buquê ocupa ao abrir (zoom de produto)

function setupPostProcessing() {
  if (!renderer.isWebGPURenderer || !studioEnvironment || !studioBackgroundTex) return null;

  const pipeline = new THREE.RenderPipeline(renderer);
  pipeline.outputColorTransform = false;

  // SSS e AO precisam de depth/normal de um pass independente: usar o próprio
  // scenePass seria dependência circular (ciclo causa QuadMesh.matrixWorld undefined).
  // Igual aos exemplos oficiais webgpu_postprocessing_sss e webgpu_postprocessing_ao.
  const prePass = pass(scene, camera);
  prePass.transparent = false; // só geometria opaca no depth (o chão de sombra é transparente)
  prePass.setMRT(mrt({ output: packNormalToRGB(normalView) }));
  prePass.getTexture('output').type = THREE.UnsignedByteType;
  const preDepth = prePass.getTextureNode('depth');
  const preNormal = sample((uv) => unpackRGBToNormal(prePass.getTextureNode().sample(uv)));

  // GTAO: escurece o miolo do buquê (vãos entre tulipas, cachos e hastes), onde a luz
  // ambiente mal chega. Entra só na luz indireta via builtinAOContext; raio em metros na
  // escala do buquê. O ruído temporal é resolvido pelo TRAA.
  const aoPass = ao(preDepth, preNormal, camera);
  aoPass.resolutionScale = 0.5;
  aoPass.samples.value = 16;
  aoPass.radius.value = 0.1;
  aoPass.useTemporalFiltering = true;
  // O GTAO sai ruidoso (meia resolução, 16 amostras) e o TRAA sozinho deixava granulado nas
  // sombras. Denoise que respeita bordas (profundidade e normal), como no exemplo oficial
  // webgpu_postprocessing_ao: suaviza sem vazar a sombra de uma haste para a outra. Vira textura
  // para ser lido no pass da cena pela posição na tela.
  const aoSmooth = convertToTexture(denoise(aoPass.getTextureNode(), preDepth, preNormal, camera));
  const aoSample = aoSmooth.sample(screenUV).r;

  // Sombras de contato entre os balões (tulipas sobre cachos e hastes). Em metros,
  // na escala do buquê (~66 cm): o raio alcança o vão entre as peças e a espessura
  // é próxima do diâmetro de um balão.
  const sssPass = sss(preDepth, camera, sun);
  sssPass.maxDistance.value = 0.12;
  sssPass.quality.value = 0.75;
  sssPass.thickness.value = 0.03;
  sssPass.useTemporalFiltering = true;
  const sssSample = sssPass.getTextureNode().sample(screenUV).r;

  // Single scenePass MRT como no exemplo oficial webgpu_postprocessing_ssr_denoise.
  const scenePass = pass(scene, camera);
  scenePass.contextNode = builtinAOContext(aoSample, builtinShadowContext(sssSample, sun));
  scenePass.setMRT(mrt({
    output,
    diffuseColor: vec4(diffuseColor.rgb, materialMetalness),
    normal: vec4(packNormalToRGB(normalView).rgb, materialRoughness),
    velocity,
  }));

  const sceneColor = scenePass.getTextureNode('output');
  const sceneDepth = scenePass.getTextureNode('depth');
  const sceneNormalTexture = scenePass.getTextureNode('normal');
  const sceneVelocity = scenePass.getTextureNode('velocity');
  const sceneDiffuse = scenePass.getTextureNode('diffuseColor');

  // Economiza banda como no exemplo oficial.
  scenePass.getTexture('normal').type = THREE.UnsignedByteType;
  scenePass.getTexture('diffuseColor').type = THREE.UnsignedByteType;

  const sceneNormal = sample((uv) => unpackRGBToNormal(sceneNormalTexture.sample(uv).rgb));
  const sceneMetalRough = sample((uv) => vec2(
    sceneDiffuse.sample(uv).a,
    sceneNormalTexture.sample(uv).a,
  ));

  // Sem environmentNode: SSR exige HDR equirect com dados CPU (HDRLoader).
  // O PNG LDR causava o warning do setEnvMap. IBL continua via scene.environment.
  const ssrNode = ssr(sceneColor, sceneDepth, sceneNormal, {
    stochastic: true,
    diffuseNode: sceneDiffuse,
    metalnessNode: sceneDiffuse.a,
    roughnessNode: sceneNormalTexture.a,
    binaryRefine: false,
  });
  const temporalNode = temporalReproject(ssrNode, sceneDepth, sceneNormalTexture, sceneVelocity, camera, {
    mode: 'specular',
    accumulate: false,
  });
  const denoiseNode = recurrentDenoise(temporalNode, camera, {
    depth: sceneDepth,
    normal: sceneNormalTexture,
    raw: ssrNode,
    metalRoughness: sceneMetalRough,
    mode: 'specular',
    accumulate: true,
  });
  denoiseNode.alphaSource = 'raylength';
  ssrNode.setHistory(denoiseNode, sceneVelocity);
  temporalNode.setHistoryTexture(denoiseNode);

  // Máscara alfa oficial: soma reflexo só onde o raio acertou.
  const denoiseBlend = vec4(denoiseNode.rgb, ssrNode.a.greaterThan(0).toVar());
  const combined = vec4(sceneColor.rgb.add(denoiseBlend.rgb), sceneColor.a);
  // outputColorTransform=false desliga tone mapping + sRGB do pipeline inteiro:
  // sem renderOutput o modelo saía linear (rosa virava magenta, sage virava oliva).
  const aa = renderOutput(sharpen(traa(combined, sceneDepth, sceneVelocity, camera), 0));
  // Fundo composto no pipeline com máscara de depth (depth>=1 = sem geometria):
  // imagem pixel-exata no fundo, modelo pós-processado na frente. Sem depender de alpha.
  const bgUV = screenUV.sub(bgOffset).div(bgSize);
  const bgColor = texture(studioBackgroundTex).sample(bgUV);
  const isBg = sceneDepth.sample(screenUV).r.greaterThanEqual(1.0);
  // No fundo só há o chão escurecendo a foto (fundo da cena 3D); a razão entre os dois é
  // quanto de luz sobra. Verde = sombra do sol (shadowCatcher); azul = sol × AO de contato.
  // O fundo da cena 3D é a própria foto (linear): a razão é contra ela, pixel a pixel.
  const bgLinear = texture(studioBackgroundLinearTex).sample(bgUV);
  const floorCast = sceneColor.g.div(bgLinear.g.max(1e-3)).clamp(0, 1);
  const floorContact = sceneColor.b.div(bgLinear.b.max(1e-3)).div(floorCast.max(1e-3)).clamp(0, 1);
  // Sombra do sol espalhada: blur gaussiano em tela, só com pixels de chão (convolução
  // normalizada: os pixels do buquê têm peso 0 e não clareiam a sombra junto ao pé).
  const floorWeight = isBg.select(float(1), float(0));
  const castBlur = gaussianBlur(vec4(floorCast.oneMinus().mul(floorWeight), floorWeight, 0, 1),
    shadowBlurStep, SHADOW_BLUR.sigma, { resolutionScale: SHADOW_BLUR.resolutionScale });
  const castSpread = castBlur.r.div(castBlur.g.max(1e-3)).oneMinus();
  const shadowLight = castSpread.mul(floorContact.pow(contactStrength));
  // Filtro de cor final por camada (ver COLOR_GRADE).
  const grade = (color, { brightness, saturation: amount, whiteBalance }) =>
    saturation(color, amount).mul(vec3(...whiteBalance)).mul(brightness);
  const background = grade(bgColor.rgb.mul(shadowLight), COLOR_GRADE.background);
  pipeline.outputNode = vec4(isBg.select(background, grade(aa.rgb, COLOR_GRADE.bouquet)), 1);
  return pipeline;
}

// Retângulo da foto num quadro (cover × zoom, posição como background-position), em pixels do quadro.
function backgroundRect(frameWidth, frameHeight, imageWidth, imageHeight) {
  const scale = Math.max(frameWidth / imageWidth, frameHeight / imageHeight) * STUDIO_BACKGROUND.zoom;
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  const [px, py] = STUDIO_BACKGROUND.position;
  return { x: (frameWidth - width) * px, y: (frameHeight - height) * py, width, height };
}

function updateBgCover() {
  if (!studioBackgroundTex?.image || !card?.clientWidth || !card?.clientHeight) return;
  const { clientWidth: w, clientHeight: h } = card;
  const rect = backgroundRect(w, h, studioBackgroundTex.image.width, studioBackgroundTex.image.height);
  bgSize.value.set(rect.width / w, rect.height / h);
  bgOffset.value.set(rect.x / w, rect.y / h);
  // Passo do blur da sombra: desvio padrão = SHADOW_BLUR.spread × altura do quadro, em qualquer tela.
  const blurHeight = renderer.domElement.height * SHADOW_BLUR.resolutionScale;
  shadowBlurStep.value = SHADOW_BLUR.spread * blurHeight / ((3 + 2 * SHADOW_BLUR.sigma) / 3);
}

function fit() {
  const { clientWidth: w, clientHeight: h } = card;
  if (!w || !h) return;
  renderer.setSize(w, h, false);
  camera.aspect = w / h;
  camera.setFocalLength(CAMERA_FOCAL_MM);
  if (modelBounds) {
    // Enquadra a altura real do buquê e o raio horizontal que ele varre ao girar, não a
    // esfera envolvente (que sobra muito num buquê alto e fino). Em telas estreitas o
    // raio horizontal passa a limitar.
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
    const size = modelBox.getSize(new THREE.Vector3());
    const radiusXZ = Math.hypot(size.x, size.z) / 2;
    const distance = Math.max(size.y / 2 / tanHalf, radiusXZ / (tanHalf * camera.aspect)) / FRAME_FILL;
    const direction = camera.position.clone().sub(controls.target).normalize();
    controls.target.copy(modelBounds.center);
    camera.position.copy(direction.multiplyScalar(distance).add(controls.target));
    controls.minDistance = distance * 0.8;
    controls.maxDistance = distance * 1.5;
    const r = modelBounds.radius * 1.6; // sombra justa no buquê
    Object.assign(sun.shadow.camera, { left: -r, right: r, top: r, bottom: -r, far: r * 8 });
    sun.shadow.camera.updateProjectionMatrix();
    camera.near = distance / 100;
    camera.far = controls.maxDistance + modelBounds.radius * 2;
    controls.update();
  }
  updateBgCover();
  camera.updateProjectionMatrix();
}
new ResizeObserver(fit).observe(card);
fit();

const draco = new DRACOLoader();
draco.setDecoderPath("https://cdn.jsdelivr.net/npm/three@0.186.0/examples/jsm/libs/draco/");
const loader = new GLTFLoader();
loader.setDRACOLoader(draco);

const byName = {};
// Látex TSL (NodeMaterial) com verniz + sheen: reflexo de estúdio visível.
function balloonify(mat) {
  const Material = renderer.isWebGPURenderer ? MeshPhysicalNodeMaterial : THREE.MeshPhysicalMaterial;
  const phys = new Material({
    name: mat.name,
    color: mat.color.clone(),
    side: mat.side,
    roughness: 0.3,
    metalness: 0,
    transmission: 0,
    ior: LATEX_IOR,
    clearcoat: 0.6,
    clearcoatRoughness: 0.15,
    specularIntensity: 1,
    sheen: 0.2,
    sheenRoughness: 0.5,
    sheenColor: new THREE.Color(0xffffff),
  });
  return phys;
}
// Acabamentos (LATEX + FINISHES) em finishes.js, compartilhado com o gerador de AR da AWS.
function applyFinish(mat, finish) {
  // ior explícito: sem ele, sair do Crystal (1.3) deixava o látex com o IOR do vidro.
  const preset = { ...LATEX, ior: LATEX_IOR, ...(FINISHES[finish] ?? FINISHES.standard) };
  // Transmissão/iridescência mudam o shader: recompila só quando ligam ou desligam.
  const recompile = (mat.transmission > 0) !== (preset.transmission > 0) || (mat.iridescence > 0) !== (preset.iridescence > 0);
  Object.assign(mat, preset);
  mat.userData.finish = FINISHES[finish] ? finish : "standard"; // lido pelo link do AR (arSpec)
  if (recompile) mat.needsUpdate = true;
}
function applyColor(part, hex, finish) {
  const mat = byName[PART_MATERIALS[part]];
  if (!mat) return;
  mat.color.set(hex);
  if (FINISHES[finish]?.transmission) {
    // Crystal: o HEX é a cor vista sobre o estúdio; o vidro multiplica o que está atrás pela
    // cor do material, então ela é a cor vista ÷ fundo médio (backdropTint), até 1 (Clear = incolor).
    const t = backdropTint.value;
    mat.color.setRGB(Math.min(1, mat.color.r / t.r), Math.min(1, mat.color.g / t.g), Math.min(1, mat.color.b / t.b));
  }
  applyFinish(mat, finish);
}
// Acabamento de um valor da paleta: metálico pelo prefixo, demais pela linha do fabricante.
const finishOf = (value) => value.startsWith("metallic-") ? "metallic"
  : (typeof PRODUCT_CONFIG !== "undefined" && PRODUCT_CONFIG.colorSeries?.[value]) || "standard";

// Cores de todas as partes vindas do app.js (fonte única, inclusive o vínculo pétalas → laço).
function applyParts(parts) {
  for (const [part, { hex, value }] of Object.entries(parts)) applyColor(part, hex, finishOf(value));
}

// Realidade aumentada: cópia do buquê com materiais glTF padrão (MeshPhysicalMaterial) nas cores
// e acabamentos atuais, em escala real (altura por tamanho em PRODUCT_CONFIG.ar.heights, metros),
// base no chão (y = 0) e centrado. É o modelo-base do gerador de AR hospedado (aws/ar-service):
// o nó raiz leva em extras a altura exportada e os materiais das partes na ordem do arSpec.
const arVector = new THREE.Vector3();
const arHeights = () => (typeof PRODUCT_CONFIG !== "undefined" && PRODUCT_CONFIG.ar?.heights) || {};
const arMaterialNames = () =>
  ((typeof PRODUCT_CONFIG !== "undefined" && PRODUCT_CONFIG.enabledParts) || Object.keys(PART_MATERIALS))
    .map((part) => PART_MATERIALS[part]);
// Configuração do AR hospedado: altura em cm + cor (HEX sRGB do material, já com a correção do
// Crystal) e acabamento de cada parte, na ordem de arMaterialNames(). Ex.: h50_ffa7c6s_90a345s_90a345s_ffa7c6t.
// Com elevação (metros), "altura de mesa": h50e85_… = buquê flutuando a 85 cm do piso.
function arSpec(size, elevation = 0) {
  const cm = Math.round((arHeights()[size] ?? 0.5) * 100) + (elevation ? `e${Math.round(elevation * 100)}` : "");
  const parts = arMaterialNames().map((name) => byName[name]);
  if (parts.some((mat) => !mat)) return null;
  return `h${cm}_` + parts.map((mat) => mat.color.getHexString() + FINISH_CODES[mat.userData.finish ?? "standard"]).join("_");
}
async function exportARModel(size) {
  const { GLTFExporter } = await import("three/addons/exporters/GLTFExporter.js");
  const copy = productModel.clone(true);
  const materials = new Map();
  const toGltf = (m) => {
    if (!materials.has(m)) {
      materials.set(m, new THREE.MeshPhysicalMaterial({
        name: m.name, color: m.color?.clone(), side: m.side, map: m.map ?? null, normalMap: m.normalMap ?? null,
        roughness: m.roughness ?? 0.5, metalness: m.metalness ?? 0, ior: m.ior ?? 1.5,
        clearcoat: m.clearcoat ?? 0, clearcoatRoughness: m.clearcoatRoughness ?? 0,
        sheen: m.sheen ?? 0, sheenRoughness: m.sheenRoughness ?? 1, sheenColor: m.sheenColor?.clone(),
        transmission: m.transmission ?? 0, thickness: m.thickness ?? 0, iridescence: m.iridescence ?? 0,
      }));
    }
    return materials.get(m);
  };
  copy.traverse((o) => {
    if (o.isMesh) o.material = Array.isArray(o.material) ? o.material.map(toGltf) : toGltf(o.material);
  });
  const root = new THREE.Group();
  root.name = "FlowersPop bouquet";
  root.add(copy);
  root.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(copy);
  const center = box.getCenter(arVector);
  copy.position.sub(new THREE.Vector3(center.x, box.min.y, center.z));
  const height = arHeights()[size] ?? box.max.y - box.min.y;
  root.scale.setScalar(height / (box.max.y - box.min.y));
  root.userData.flowerspop = { heightMeters: height, parts: arMaterialNames() }; // vira extras no GLB
  const glb = await new GLTFExporter().parseAsync(root, { binary: true });
  return new Blob([glb], { type: "model/gltf-binary" });
}

const viewerStatus = document.querySelector('#viewer-status');
const rotateButton = document.querySelector('#auto-rotate');
const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();
let productModel;
let pointerStart;
let moved = false;
let activePointers = new Set();

function stopRotation() {
  cancelInteractionHint();
  controls.autoRotate = false;
  rotateButton.setAttribute('aria-pressed', 'false');
}

// Dica de interação, como o interaction prompt do <model-viewer>: depois de carregar, uma mãozinha
// balança sobre o preview e o buquê gira um pouco junto, mostrando que dá para arrastar e girar.
// Duas oscilações e volta exatamente ao enquadramento; some no primeiro toque, arraste, zoom ou
// botão de vista. Com "reduzir movimento" no sistema, a mão aparece parada.
const INTERACTION_HINT = { delayMs: 1200, cycles: 2, periodMs: 2400, angle: THREE.MathUtils.degToRad(20), handPx: 44 };
const HAND_ICON = '<svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="21" cy="11" r="8" fill="none" stroke="#fff" stroke-width="2.2" opacity=".85"/>' +
  '<path d="M18 11c0-1.7 1.3-3 3-3s3 1.3 3 3v11.5l1-.3c.1-1.5 1.4-2.7 3-2.7s2.9 1.2 3 2.7l.8-.2c.3-1.3 1.5-2.3 2.9-2.3 1.7 0 3 1.3 3 3V33c0 5.5-4.5 10-10 10h-3.2c-3.3 0-6.3-1.8-7.9-4.7l-4.4-8.1c-.8-1.4-.3-3.2 1.1-4 1.3-.7 2.9-.4 3.8.8L18 30z" fill="#fff" stroke="#242426" stroke-width="1.6" stroke-linejoin="round"/></svg>';
const UP = new THREE.Vector3(0, 1, 0);
let interactionHint = null;
let userInteracted = false;
function playInteractionHint() {
  if (userInteracted || interactionHint || exporting || !card) return;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hand = document.createElement('div');
  hand.className = 'interaction-hint';
  hand.innerHTML = HAND_ICON;
  card.append(hand);
  const offset = camera.position.clone().sub(controls.target);
  const start = performance.now();
  const total = INTERACTION_HINT.cycles * INTERACTION_HINT.periodMs;
  let frame = 0;
  const end = (restore) => {
    cancelAnimationFrame(frame);
    if (restore && !reduceMotion) camera.position.copy(controls.target).add(offset);
    hand.classList.add('is-leaving');
    setTimeout(() => hand.remove(), 400);
    interactionHint = null;
  };
  const step = (now) => {
    const t = now - start;
    if (t >= total) return end(true);
    const swing = reduceMotion ? 0 : Math.sin((t / INTERACTION_HINT.periodMs) * Math.PI * 2);
    hand.style.setProperty('--hint-x', `${(swing * INTERACTION_HINT.handPx).toFixed(1)}px`);
    // Mão para a direita = buquê girando para a direita (a câmera orbita para a esquerda).
    if (!reduceMotion) camera.position.copy(controls.target).add(offset.clone().applyAxisAngle(UP, -swing * INTERACTION_HINT.angle));
    frame = requestAnimationFrame(step);
  };
  frame = requestAnimationFrame(step);
  interactionHint = { end };
}
// restore = volta ao enquadramento de antes da dica (captura da imagem); o toque do cliente não.
function cancelInteractionHint(restore = false) {
  userInteracted = true;
  interactionHint?.end(restore);
}
renderer.domElement.addEventListener('wheel', () => cancelInteractionHint(), { passive: true });

// A drag or pinch must never select a part on release.
renderer.domElement.addEventListener('pointerdown', (event) => {
  stopRotation();
  activePointers.add(event.pointerId);
  if (activePointers.size === 1) {
    pointerStart = [event.clientX, event.clientY];
    moved = false;
  } else moved = true;
});
renderer.domElement.addEventListener('pointermove', (event) => {
  if (pointerStart && Math.hypot(event.clientX - pointerStart[0], event.clientY - pointerStart[1]) > 6) moved = true;
});
renderer.domElement.addEventListener('pointercancel', (event) => {
  activePointers.delete(event.pointerId);
  moved = true;
  if (!activePointers.size) pointerStart = null;
});
renderer.domElement.addEventListener('pointerup', (event) => {
  activePointers.delete(event.pointerId);
  if (activePointers.size) return;
  const wasTap = pointerStart && !moved && event.button === 0;
  pointerStart = null;
  if (!wasTap || !productModel) return;
  const rect = renderer.domElement.getBoundingClientRect();
  pointer.set((event.clientX - rect.left) / rect.width * 2 - 1, -(event.clientY - rect.top) / rect.height * 2 + 1);
  raycaster.setFromCamera(pointer, camera);
  const hit = raycaster.intersectObject(productModel, true)[0];
  if (!hit) return;
  const material = Array.isArray(hit.object.material) ? hit.object.material[hit.face.materialIndex] : hit.object.material;
  const part = Object.keys(PART_MATERIALS).find((key) => PART_MATERIALS[key] === material.name);
  const input = form.querySelector(`input[name="part"][value="${part}"]`);
  if (input) {
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    viewerStatus.textContent = `Editing ${input.value}. Choose a color in the palette.`;
  }
});

const VIEW_DIRECTIONS = { front: [0, 0, 1], back: [0, 0, -1], left: [-1, 0, 0], right: [1, 0, 0] };
// Câmera numa vista exata, no enquadramento padrão (fit refaz a distância: sem o zoom do cliente).
function setView(view) {
  // Flush OrbitControls inertia before choosing an exact camera direction.
  controls.enableDamping = false;
  controls.update();
  camera.position.copy(controls.target).add(new THREE.Vector3(...VIEW_DIRECTIONS[view]));
  fit();
  controls.enableDamping = true;
}
document.querySelectorAll('[data-view]').forEach((button) => {
  button.addEventListener('click', () => {
    stopRotation();
    setView(button.dataset.view);
  });
});
rotateButton.addEventListener('click', () => {
  cancelInteractionHint();
  controls.autoRotate = !controls.autoRotate;
  controls.autoRotateSpeed = 0.6;
  rotateButton.setAttribute('aria-pressed', String(controls.autoRotate));
});
// Painel de ajuste: mexe ao vivo na luz principal e na sombra do chão. Oculto por padrão;
// abrir a página com ?tune para usar (uso interno, nunca aparece para o cliente).
const tuneParam = new URLSearchParams(location.search).get("tune");
if (tuneParam !== null && tuneParam !== "0") mountTuning();
async function mountTuning() {
  const { mountTuningPanel } = await import("./flowerspop-tuning-panel.js?v=792c694a3a");
  const angles = SUN_ANGLES;
  const aim = aimSun;
  const round = (v, step) => Math.round(v / step) * step;
  mountTuningPanel([
    // azimute: 0 = luz de frente, negativo = vem da esquerda (sombra para a direita)
    { key: "lightAzimuth", label: "Luz: direção (°)", min: -180, max: 180, step: 1, value: round(angles.azimuth, 1), set: (v) => { angles.azimuth = v; aim(); } },
    { key: "lightElevation", label: "Luz: altura (°)", min: 5, max: 89, step: 1, value: round(angles.elevation, 1), set: (v) => { angles.elevation = v; aim(); } },
    { key: "sunIntensity", label: "Luz: intensidade", min: 0, max: 6, step: 0.1, value: sun.intensity, set: (v) => { sun.intensity = v; } },
    { key: "fillIntensity", label: "Luz de preenchimento", min: 0, max: 3, step: 0.05, value: fill.intensity, set: (v) => { fill.intensity = v; } },
    { key: "studioIntensity", label: "Luz ambiente (HDRI)", min: 0, max: 2, step: 0.05, value: scene.environmentIntensity, set: (v) => { scene.environmentIntensity = v; } },
    { key: "hdriRotation", label: "HDRI: rotação (°)", min: -180, max: 180, step: 5, value: STUDIO_HDRI.rotation, set: (v) => { scene.environmentRotation.y = THREE.MathUtils.degToRad(v); } },
    { key: "skyIntensity", label: "Luz de céu", min: 0, max: 2, step: 0.05, value: hemi.intensity, set: (v) => { hemi.intensity = v; } },
    { key: "exposure", label: "Exposição", min: 0.5, max: 2, step: 0.05, value: renderer.toneMappingExposure, set: (v) => { renderer.toneMappingExposure = v; } },
    { key: "shadowDensity", label: "Sombra: densidade", min: 0, max: 1, step: 0.01, value: shadowCatcher.material.opacity, set: (v) => { shadowCatcher.material.opacity = v; } },
    { key: "shadowSpread", label: "Sombra: espalhamento", min: 0, max: 0.12, step: 0.005, value: SHADOW_BLUR.spread, set: (v) => { SHADOW_BLUR.spread = v; updateBgCover(); } },
    { key: "contactAO", label: "AO de contato", min: 0, max: 3, step: 0.05, value: contactStrength.value, set: (v) => { contactStrength.value = v; } },
  ]);
}

// Imagem salva (Save image / PDF) em resolução de impressão, independente da tela, no enquadramento
// do preview: 4K no computador (lado maior 3840 px ≈ 32 cm a 300 dpi); no celular 2400 px, pela
// memória da GPU. O canvas cresce só durante a captura. Os efeitos temporais (TRAA, AO, reflexos)
// precisam de quadros na resolução nova, com a câmera parada, para convergir sem granulado — mais
// quadros, imagem mais limpa. Eles só avançam a cada requestAnimationFrame (NodeFrame do three),
// então a espera tem teto: num aparelho lento ou numa aba oculta sai com o que acumulou até maxWaitMs.
const coarsePointer = matchMedia('(pointer: coarse)').matches;
const maxTextureSize = renderer.backend?.device?.limits?.maxTextureDimension2D ?? renderer.capabilities?.maxTextureSize ?? 4096;
const EXPORT_IMAGE = { longSide: Math.min(coarsePointer ? 2400 : 3840, maxTextureSize), settleFrames: 64, maxWaitMs: 9000 };
let exporting = null;
function exportCanvas() {
  exporting ??= renderExportCanvas().finally(() => { exporting = null; });
  return exporting;
}
async function renderExportCanvas() {
  cancelInteractionHint(true); // a dica não pode mexer a câmera durante a captura
  const screenRatio = renderer.getPixelRatio();
  const longSide = Math.max(card.clientWidth, card.clientHeight);
  const wasRotating = controls.autoRotate;
  controls.autoRotate = false;
  // A imagem salva (e a foto do PDF) é sempre de frente, no enquadramento padrão, qualquer que seja
  // o ângulo ou o zoom em que o cliente deixou o 3D; depois a câmera volta para onde estava.
  const userView = { position: camera.position.clone(), target: controls.target.clone() };
  controls.enabled = false; // nenhum arraste mexe na câmera durante a captura
  setView('front');
  controls.enableDamping = false; // sem inércia: a câmera fica parada para os efeitos convergirem
  if (longSide) renderer.setPixelRatio(Math.max(screenRatio, EXPORT_IMAGE.longSide / longSide));
  updateBgCover(); // o blur da sombra do chão é proporcional à altura do canvas em pixels
  try {
    // O loop de animação segue renderizando; aqui só esperamos os quadros na resolução nova.
    // O progresso (quadros ou tempo, o que acabar primeiro) vai para o aviso de exportação (app.js).
    const start = performance.now();
    const deadline = start + EXPORT_IMAGE.maxWaitMs;
    const progress = (value) => form.dispatchEvent(new CustomEvent('flowerspop:export-progress', { detail: { capture: Math.min(1, value) } }));
    for (let i = 0; i < EXPORT_IMAGE.settleFrames && performance.now() < deadline; i++) {
      await new Promise((resolve) => {
        requestAnimationFrame(resolve);
        setTimeout(resolve, 250); // rAF pausado (aba oculta) não pode travar a captura
      });
      progress(Math.max((i + 1) / EXPORT_IMAGE.settleFrames, (performance.now() - start) / EXPORT_IMAGE.maxWaitMs));
    }
    progress(1);
    // Mesmo pipeline do preview (AO, SSS, reflexos, fundo composto): a imagem salva sai igual à tela.
    if (postProcessing) postProcessing.render();
    else renderer.render(scene, camera);
    const snapshot = document.createElement('canvas');
    snapshot.width = renderer.domElement.width;
    snapshot.height = renderer.domElement.height;
    const context = snapshot.getContext('2d');
    if (studioBackground.complete && studioBackground.naturalWidth) {
      const { x, y, width, height } = backgroundRect(snapshot.width, snapshot.height, studioBackground.naturalWidth, studioBackground.naturalHeight);
      context.drawImage(studioBackground, x, y, width, height);
    } else {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, snapshot.width, snapshot.height);
    }
    context.drawImage(renderer.domElement, 0, 0);
    return snapshot;
  } finally {
    renderer.setPixelRatio(screenRatio);
    updateBgCover();
    controls.target.copy(userView.target);
    camera.position.copy(userView.position);
    controls.update();
    controls.enableDamping = true;
    controls.enabled = true;
    controls.autoRotate = wasRotating;
  }
}
const captureImage = async () => (await exportCanvas()).toDataURL('image/png');
document.querySelector('#download-image').addEventListener('click', async (event) => {
  const button = event.currentTarget;
  button.disabled = true;
  const previousStatus = viewerStatus.textContent;
  const title = 'Preparing your image for download…';
  viewerStatus.textContent = title;
  // Etapas para o aviso de exportação (logo + barra, em app.js): o clique responde na hora.
  const notify = (state) => form.dispatchEvent(new CustomEvent('flowerspop:export', { detail: { kind: 'image', state, title } }));
  notify('start');
  try {
    const snapshot = await exportCanvas();
    notify('encoding');
    // Blob em vez de data URL: o PNG em resolução de impressão tem vários MB.
    const blob = await new Promise((resolve, reject) => {
      snapshot.toBlob((result) => (result ? resolve(result) : reject(new Error('PNG encoding failed'))), 'image/png');
    });
    const link = document.createElement('a');
    const file = {};
    form.dispatchEvent(new CustomEvent('flowerspop:file-name', { detail: file })); // cores de cada peça (app.js)
    link.download = `${file.name || 'flowerspop-bouquet'}.png`;
    link.href = URL.createObjectURL(blob);
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 60_000);
    viewerStatus.textContent = previousStatus;
    notify('end');
  } catch (err) {
    notify('error');
    viewerStatus.textContent = 'Could not save the image. Please try again.';
    console.warn('[renderer] image export failed:', err);
  } finally {
    button.disabled = false;
  }
});

loader.load(
  assetUrl(MODEL_URL),
  async (gltf) => {
    setLoadProgress("model", 1);
    const configured = new Set(Object.values(PART_MATERIALS));
    const swap = (m) => {
      if (!configured.has(m.name)) return m;
      return (byName[m.name] ??= balloonify(m));
    };
    gltf.scene.traverse((o) => {
      if (!o.isMesh || !o.material) return;
      o.castShadow = true;
      o.receiveShadow = true;
      o.material = Array.isArray(o.material) ? o.material.map(swap) : swap(o.material);
    });
    // Malhas sem material configurável que pertencem a uma parte (PRODUCT_CONFIG.meshParts).
    const meshParts = (typeof PRODUCT_CONFIG !== "undefined" && PRODUCT_CONFIG.meshParts) || {};
    for (const [meshName, part] of Object.entries(meshParts)) {
      const mesh = gltf.scene.getObjectByName(meshName);
      const mat = byName[PART_MATERIALS[part]];
      if (mesh?.isMesh && mat) mesh.material = mat;
    }
    modelBox = new THREE.Box3().setFromObject(gltf.scene);
    modelBounds = modelBox.getBoundingSphere(new THREE.Sphere());
    // Buquê apoiado: o chão fica na ponta das hastes, a sombra de contato nasce embaixo delas.
    ground.position.set(modelBounds.center.x, modelBox.min.y, modelBounds.center.z);
    shadowCatcher.scale.setScalar(modelBounds.radius * 8);
    camera.position.set(0, 0, 1).add(controls.target);
    fit();
    alignLights();
    scene.add(gltf.scene, ground);
    const contactAO = bakeContactAO(gltf.scene, modelBox.min.y); // coordenadas de mundo, fixo no buquê
    if (contactAO) scene.add(contactAO);
    productModel = gltf.scene;
    document.querySelectorAll('.view-controls button').forEach((button) => { button.disabled = false; });
    viewerStatus.textContent = 'Live preview: recolor your bouquet in real time';
    form.addEventListener('flowerspop:capture', (event) => {
      event.detail.image = captureImage();
    });
    form.addEventListener('flowerspop:export-ar', (event) => {
      event.detail.glb = exportARModel(event.detail.size);
    });
    form.addEventListener('flowerspop:ar-spec', (event) => {
      event.detail.spec = arSpec(event.detail.size, event.detail.elevation);
    });
    document.querySelector('#view-in-ar')?.removeAttribute('disabled');
    form.dispatchEvent(new Event('flowerspop:ar-ready'));
    photo?.remove();
    await studioEnvReady; // a tinta do Crystal depende da cor média do fundo
    const current = {};
    form.dispatchEvent(new CustomEvent("flowerspop:get-parts", { detail: current }));
    if (current.parts) applyParts(current.parts);
    postProcessing = setupPostProcessing();
    let firstFrame = true;
    renderer.setAnimationLoop(() => {
      controls.update();
      alignLights();
      if (postProcessing) postProcessing.render();
      else renderer.render(scene, camera);
      if (firstFrame) {
        firstFrame = false;
        showLoadProgress(1);
        previewLoader?.classList.add("is-done");
        setTimeout(playInteractionHint, INTERACTION_HINT.delayMs);
      }
    });
  },
  byteProgress("model"),
  (err) => {
    viewerStatus.textContent = 'The 3D preview could not load. Reload to try again.';
    card?.classList.add("is-failed"); // mostra a foto do produto no lugar da tela de carregamento
    console.warn('[renderer] GLB load failed, static photo kept:', err);
  },
);

form?.addEventListener("flowerspop:parts", (event) => applyParts(event.detail));
form?.addEventListener("reset", stopRotation);
